'use strict'
import PeakRow from './PeakRow.js';
import InjectionRow from './InjectionRow.js';
import PeakRowProxy from './proxy/PeakRowProxy.js';
import InjectionRowProxy from './proxy/InjectionRowProxy.js';
import TableColumn, {COLUMN_TYPE} from "./TableColumn.js";
import {CsvUtils} from "../../util/CsvUtils.js";

export class VizData {
	/**
	 * @param {DataTable} dataTable
	 * @param props
	 */
	constructor(dataTable, props = {}) {
		this._dataTable = dataTable;
		this._rows = [];
		this._filterCriteria = props.filter;
		this._sortCriteria = null;
		this._version = -1;
		this._callbacks = [];
		this._converters = props.converters || {};
		this._updateRows();
	}

	/**
	 * @param {FilterCriteria} criteria
	 */
	setFilter(criteria) {
		this._filterCriteria = criteria;
		this._version = -1;
		this._updateRows();
	}

	getFilterCriteria(){
		return this._filterCriteria;
	}

	/**
	 * @param {TableRowComparator} comparator
	 */
	setSorting(comparator) {
		this._sortCriteria = comparator;
		this._version = -1;
	}

	getRowObject(rowNumber) {
		return this._getActualRows()[rowNumber];
	}

	getRowsCount() {
		return this._getActualRows().length;
	}

	/**
	 * @returns {TableColumns}
	 */
	getColumns() {
		return this._dataTable.columns
	}

	addColumn(column) {
		this._dataTable.addColumn(column);
		const {fullChange} = this._updateRows();
		if (!fullChange) {
			if (this._callbacks['column-added'])
				this._callbacks['column-added'].forEach(callback => callback(column));
			this._fireDependenciesChangedEvent(this._dataTable.columns.getColumnsDependsOn(column.getDisplayName()));
		}
	}

	moveColumn(index, newIndex) {
		this._dataTable.columns.moveColumn(index, newIndex);
		if (this._callbacks['column-moved'])
			this._callbacks['column-moved'].forEach(callback => callback(index, newIndex));
	}

	updateColumn(index, props) {
		const col = this._dataTable.columns.getByIndex(index);
		const name = props.name || col.getDisplayName();
		const expression = props.expression || col.getExpression();
		let autoDetectType = col.isColumnTypeAutoDetectionEnabled();
		if (props.autoDetectType !== undefined)
			autoDetectType = props.autoDetectType;
		else if (expression !== col.getExpression())
			autoDetectType = true;
		const width = props.width || col.getDisplayOptions().width;
		const type = props.type || col.getDisplayOptions().type;
		const update = new TableColumn(name, expression, {width, type, autoDetectType});
		this._dataTable.updateColumn(col.getDisplayName(), update);
		const {fullChange} = this._updateRows();
		if (!fullChange) {
			if (this._callbacks['column-updated'])
				this._callbacks['column-updated'].forEach(callback => callback(index, update));
			let colsToUpdate = new Set([...this._dataTable.columns.getColumnsDependsOn(col.getDisplayName()), ...this._dataTable.columns.getColumnsDependsOn(update.getDisplayName())]);
			this._fireDependenciesChangedEvent(Array.from(colsToUpdate));
		}
	}

	deleteColumn(index) {
		if (this.getColumns().length() === 1)
			throw new Error("Can't delete the last column, at least one column should remain");
		const col = this._dataTable.columns.getByIndex(index);
		this._dataTable.removeColumn(col.getDisplayOptions().name);
		const {fullChange} = this._updateRows();
		if (!fullChange) {
			if (this._callbacks['column-deleted'])
				this._callbacks['column-deleted'].forEach(callback => callback(index));
			this._fireDependenciesChangedEvent(this._dataTable.columns.getColumnsDependsOn(col.getDisplayName()));
		}
	}

	getCells(rowIndex) {
		const row = this.getRowObject(rowIndex);
		return this.getColumns()._columns.map((column) => this._getCell(row, column));
	}

	getCell(rowIndex, columnIndex) {
		return this._getCell(this.getRowObject(rowIndex), this._dataTable.columns.getByIndex(columnIndex));
	}

	getValues(rowNumber, columns) {
		const row = this.getRowObject(rowNumber);
		if (!row)
			return []
		return columns.map(c => row.getDisplayValue(c))
	}

	toCsv(){
		const columnNames = this.getColumns().getNames();
		const data = this._getActualRows().map(row => columnNames.map(name=>row.col[name]));
		data.unshift(columnNames);
		return CsvUtils.array2CsvConverter.convert(data);
	}

	get version(){
		return this._version
	}

	on(eventName, callback) {
		if (!this._callbacks[eventName])
			this._callbacks[eventName] = [];
		this._callbacks[eventName].push(callback);
	}

	_getActualRows() {
		if (this._dataTable.version !== this._version || this._dataTable.version === -1)
			this._updateRows();
		return this._rows
	}

	_updateRows() {
		let rows = [], rowsChanged, lengthChanged;
		if (this._dataTable.columns.length() > 0) {
			rows = this._dataTable.rows.slice();
			if (this._filterCriteria)
				rows = rows.filter(d => this._filterCriteria.match(d))
			if (this._sortCriteria)
				rows = rows.sort((a, b) => this._sortCriteria.compare(a, b))
			lengthChanged = this._rows.length !== rows.length;
			if (!lengthChanged) {
				for (let i = 0; i < this._rows.length; i++) {
					if (this._rows[i].id !== rows[i].id) {
						rowsChanged = true;
						break;
					}
				}
			}
		}
		this._rows = rows;
		this._version = this._dataTable.version;
		if (lengthChanged || rowsChanged) {
			if (this._callbacks['full-change'])
				this._callbacks['full-change'].forEach(callback => callback());
		}
		return {fullChange: lengthChanged || rowsChanged}
	}

	/**

	 * @param {PeakRowProxy|InjectionRowProxy} row
	 * @param {TableColumn} column
	 * @returns {{value: *, type: string}}
	 */
	_getCell(row, column) {
		const type = column.getType();
		const converter = this._converters[type];
		const rawValue = row.getDisplayValue(column.getDisplayName());
		return {value: converter ? converter(rawValue) : rawValue, type}
	}

	_fireDependenciesChangedEvent(columnNames) {
		const r = this.getColumns().getIndexedColumns(columnNames);
		for (let i = 0; i < r.indices.length; i++) {
			if (this._callbacks["column-updated"])
				this._callbacks["column-updated"].forEach(callback => callback(r.indices[i], r.cols[i]));
		}
	}
}

export class DataTable {
	/**
	 * @param {InjectionRow[] | PeakRow[]} rows
	 * @param {TableColumns} columns
	 */
	constructor(rows, columns) {
		this._rows = DataTable._makeProxy(rows);
		this._columns = columns;
		this._version = 1;
		this._updateRows();
	}

	get version() {
		return this._version;
	}

	get columns() {
		return this._columns;
	}

	get rows() {
		return this._rows
	}

	/**
	 * @param {TableColumn} column
	 */
	addColumn(column) {
		this._columns.add(column);
		this._updateRows(column);
		this._version++;
	}

	/**
	 * @param {string} columnName
	 * @param {TableColumn} column
	 */
	updateColumn(columnName, column) {
		this._columns.update(columnName, column);
		let updateFrom = columnName === column.getDisplayName() ? column : null;
		if (!updateFrom)
			for (const row of this._rows)
				delete row.col[columnName];
		this._updateRows(updateFrom);
		this._version++;
	}

	/**
	 * @param {string} columnName
	 */
	removeColumn(columnName) {
		this._columns.remove(columnName);
		for (const row of this._rows)
			delete row.col[columnName];
		this._updateRows();
		this._version++;
	}

	/**
	 * @param {TableColumn?} fromColumn
	 * @return string[]
	 */
	_updateRows(fromColumn) {
		let columnsToEvaluate = [];
		if (!fromColumn)
			columnsToEvaluate = this._columns._graph.getVerticesInReverseOrder();
		else {
			const queue = [this._columns._graph.getVertex(fromColumn.getDisplayName())];
			while (queue.length > 0) {
				const current = queue.shift();
				columnsToEvaluate.push(current.obj);
				for (const edge of current.edges) {
					if (edge.to === current) {
						queue.push(edge.from)
					}
				}
			}
		}
		for (const vertex of columnsToEvaluate) {
			const column = this._columns.findByName(vertex);
			if (column)
				this.#setSafeColumnValues(column);
		}
		return columnsToEvaluate;
	}

	/** @param {TableColumn} column */
	#setSafeColumnValues(column){
		const name = column.getDisplayName();
		const expression = column.getExpression();
		const isEvaluable = column.getMissingDependencies().length === 0;
		const autoDetectType = column.isColumnTypeAutoDetectionEnabled();
		let columnType = COLUMN_TYPE.UNKNOWN;
		for (const row of this._rows) {
			let valueForCalculation = '';
			let evaluatedValue = '';
			if (isEvaluable) {
				evaluatedValue = column.evaluate(row);
				valueForCalculation = this._safeValueForCalculate(evaluatedValue, column.getType());
				if (autoDetectType && columnType !== COLUMN_TYPE.MIXED) {
					const valueType = COLUMN_TYPE.isSvgExpression(expression) ? COLUMN_TYPE.SVG : COLUMN_TYPE.resolve(valueForCalculation);
					if (columnType === COLUMN_TYPE.UNKNOWN)
						columnType = valueType;
					else if (columnType !== valueType && valueForCalculation)
						columnType = COLUMN_TYPE.MIXED;
				}
			}
			row.col[name] = valueForCalculation;
			if (valueForCalculation !== evaluatedValue) {
				row.setDisplayValue(name, this._safeValueForTable(evaluatedValue));
			} else {
				// If value is equal we delete this property from displayValues
				// Otherwise, once added, the property remains in this object until the page is refreshed
				row.removeDisplayValue(name);
			}
		}
		if (column.isColumnTypeAutoDetectionEnabled()){
			if (column.isCustomColumn() && columnType !== column.getType() && columnType !== COLUMN_TYPE.MIXED
				&& columnType !== COLUMN_TYPE.UNKNOWN) {
				// In case custom user column
				// Initially, column type of these columns is "string" and autoDetectType = true
				// In case the values in the column are numbers and null/undefined, such nulls will not turn into "0"
				// for valueForCalculation (because the column type is string)
				// So we compare initial column type with columnType after checking all values,
				// and if type has been changed, then we need to recalculate the values again.
				//
				// If we add possibility to set type of custom user column when it will create,
				// we should remove code inside this if
				column.setType(columnType);
				this.#setSafeColumnValues(column);
			}
			column.setType(columnType);
		}
	}

	/**
	 * Replace null and undefined values with 0 if type or columnType is "number"
	 * because this value can be lately used in calculations
	 *
	 * @param {*} val
	 * @param {string} colType
	 * @returns {number|string|boolean}
	 * @private
	 */
	_safeValueForCalculate(val, colType){
		switch (typeof val) {
			case "boolean":
				return val
			case "string":
				return val.trim()
			case "number":
				return isNaN(val) || !isFinite(val) ? '' : val
			default: {
				if (colType === COLUMN_TYPE.NUMBERS && val == null) // if val is null or undefined
					return 0
				return ''
			}
		}
	}

	/**
	 * Do not replace null and undefined values with 0 because we don't want to get extra 0 in the table
	 *
	 * @param {*} val
	 * @returns {string|number|boolean}
	 * @private
	 */
	_safeValueForTable(val){
		switch (typeof val) {
			case "boolean":
				return val
			case "string":
				return val.trim()
			case "number":
				return isNaN(val) || !isFinite(val) ? '' : val
			default:
				return ''
		}
	}

	static _makeProxy(rows) {
		if (rows.length === 0) return [];
		const obj = rows[0];
		if (obj instanceof InjectionRow)
			return rows.map(r => new InjectionRowProxy(r))
		if (obj instanceof PeakRow)
			return rows.map(r => new PeakRowProxy(r))
		throw new Error('Unsupported row type')
	}
}

export class FilterCriteria {
	constructor(jexlExprString) {
		this._exprString = jexlExprString;
		this._jexlExpr = jexl.compile(jexlExprString);
	}

	/**
	 * Matches some 'datum', could be a row in PivotTable or point in PivotChart
	 * @param datum
	 * @return {boolean}
	 */
	match(datum) {
		// something like that
		return !!this._jexlExpr.evalSync(datum);
	}

	/**
	 * Used as template for creating the new filter criteria
	 */
	getFilterString() {
		return this._exprString;
	}
}

export class TableRowComparator {
	constructor(fn) {
		this._fn = fn ? fn : () => {
			return 0
		};
	}

	compare(a, b) {
		return this._fn(a, b)
	}

	static create(columnName, dataType, ascending) {
		const orderModification = ascending ? 1 : -1;
		switch (dataType) {
			case 'string' : {
				return new TableRowComparator((a, b) => {
					let valA = a.col[columnName];
					let valB = b.col[columnName];
					return valA.localeCompare(valB) * orderModification
				})
			}
			case 'number' : {
				return new TableRowComparator((a, b) => {
					let valA = a.col[columnName];
					let valB = b.col[columnName];
					if (+valA > +valB) return orderModification;
					if (+valA === +valB) return 0;
					return -orderModification;
				})
			}
		}
		throw new Error('Unknown data type: ' + dataType)
	}
}
